'use client';
import React from 'react';
import { useModel } from '@/context/ModelContext';
import { fmtCrShort } from '@/lib/financial-model';
import DscrTrendChart from './DscrTrendChart';
import PaybackChart from './PaybackChart';
import {
  TrendingUp,
  DollarSign,
  Clock,
  BarChart2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from 'lucide-react';

export default function ReturnsPanel() {
  const { model } = useModel();
  const a = model.assumptions;

  const projectIrrPct = model.projectIRR * 100;
  const equityIrrPct = model.equityIRR * 100;

  // DSCR summary
  const dscrYears = model.years.filter((y) => y.dscr > 0);
  const minDscr = dscrYears.length > 0 ? Math.min(...dscrYears.map((y) => y.dscr)) : 0;
  const avgDscr =
    dscrYears.length > 0 ? dscrYears.reduce((s, y) => s + y.dscr, 0) / dscrYears.length : 0;

  function irrStatus(irr: number) {
    if (irr > 0.5)
      return {
        icon: <CheckCircle2 size={16} className="text-primary" />,
        label: 'Excellent',
        variant: 'success',
      };
    if (irr > 0.2)
      return {
        icon: <CheckCircle2 size={16} className="text-accent" />,
        label: 'Strong',
        variant: 'info',
      };
    if (irr > a.discountRate)
      return {
        icon: <AlertTriangle size={16} className="text-warning" />,
        label: 'Acceptable',
        variant: 'warning',
      };
    return {
      icon: <XCircle size={16} className="text-danger" />,
      label: 'Below Hurdle',
      variant: 'danger',
    };
  }

  const projStatus = irrStatus(model.projectIRR);
  const eqStatus = irrStatus(model.equityIRR);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold text-foreground">
          Key Return Metrics — 10-Year Horizon
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          IRR, NPV, Payback, DSCR — all computed from current assumptions
        </p>
      </div>

      {/* Returns KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Project IRR */}
        <div className="kpi-card border border-border bg-success-bg col-span-1">
          <div className="flex items-start justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
              <TrendingUp size={18} className="text-primary" />
            </div>
            {projStatus.icon}
          </div>
          <p className="kpi-label">Project IRR (Unlevered)</p>
          <p className="kpi-value-lg font-mono-nums text-primary">{projectIrrPct.toFixed(1)}%</p>
          <p className="text-xs text-muted-foreground mt-1">
            vs WACC {(a.discountRate * 100).toFixed(1)}% · {projStatus.label}
          </p>
          <div className="mt-2 h-1.5 rounded-full bg-green-100">
            <div
              className="h-1.5 rounded-full bg-primary transition-all duration-500"
              style={{ width: `${Math.min(100, (projectIrrPct / 200) * 100)}%` }}
            />
          </div>
        </div>

        {/* Equity IRR */}
        <div className="kpi-card border border-border bg-info-bg">
          <div className="flex items-start justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
              <TrendingUp size={18} className="text-accent" />
            </div>
            {eqStatus.icon}
          </div>
          <p className="kpi-label">Equity IRR (Levered)</p>
          <p className="kpi-value-lg font-mono-nums text-accent">{equityIrrPct.toFixed(1)}%</p>
          <p className="text-xs text-muted-foreground mt-1">
            After debt service · {eqStatus.label}
          </p>
          <div className="mt-2 h-1.5 rounded-full bg-blue-100">
            <div
              className="h-1.5 rounded-full bg-accent transition-all duration-500"
              style={{ width: `${Math.min(100, (equityIrrPct / 400) * 100)}%` }}
            />
          </div>
        </div>

        {/* NPV */}
        <div className="kpi-card border border-border">
          <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center mb-2">
            <DollarSign size={18} className="text-muted-foreground" />
          </div>
          <p className="kpi-label">Project NPV @ {(a.discountRate * 100).toFixed(0)}%</p>
          <p
            className={`kpi-value-lg font-mono-nums ${model.projectNPV >= 0 ? 'text-primary' : 'text-danger'}`}
          >
            {fmtCrShort(model.projectNPV)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">10-yr explicit · no terminal value</p>
        </div>

        {/* Payback */}
        <div className="kpi-card border border-border">
          <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center mb-2">
            <Clock size={18} className="text-muted-foreground" />
          </div>
          <p className="kpi-label">Simple Payback Period</p>
          <p className="kpi-value-lg font-mono-nums text-foreground">
            {model.paybackYearFractional > 0
              ? `${model.paybackYearFractional.toFixed(1)} yrs`
              : 'N/A'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Project FCF basis · target &lt; 3 yrs
          </p>
        </div>
      </div>

      {/* DSCR Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div
          className={`p-4 rounded-xl border ${minDscr >= 1.5 ? 'bg-success-bg border-green-200' : 'bg-danger-bg border-red-200'}`}
        >
          <div className="flex items-center gap-2 mb-1">
            <BarChart2 size={14} className={minDscr >= 1.5 ? 'text-primary' : 'text-danger'} />
            <p className="text-xs font-semibold text-foreground">Minimum DSCR</p>
          </div>
          <p
            className={`font-mono text-xl font-bold ${minDscr >= 1.5 ? 'text-primary' : 'text-danger'}`}
          >
            {minDscr > 0 ? `${minDscr.toFixed(2)}x` : 'N/A'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {minDscr >= 2
              ? '✓ Well above 1.5x covenant'
              : minDscr >= 1.5
                ? '✓ Above 1.5x covenant'
                : '⚠ Below 1.5x covenant'}
          </p>
        </div>
        <div className="p-4 rounded-xl border bg-card border-border">
          <div className="flex items-center gap-2 mb-1">
            <BarChart2 size={14} className="text-muted-foreground" />
            <p className="text-xs font-semibold text-foreground">Average DSCR</p>
          </div>
          <p className="font-mono text-xl font-bold text-foreground">
            {avgDscr > 0 ? `${avgDscr.toFixed(2)}x` : 'N/A'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Across debt service years</p>
        </div>
        <div className="p-4 rounded-xl border bg-card border-border">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 size={14} className="text-primary" />
            <p className="text-xs font-semibold text-foreground">Bankability</p>
          </div>
          <p
            className={`font-mono text-sm font-bold mt-1 ${minDscr >= 1.5 ? 'text-primary' : 'text-warning'}`}
          >
            {minDscr >= 1.5 ? 'CGTMSE Eligible' : 'Review Required'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">DSCR covenant ≥ 1.50x</p>
        </div>
      </div>

      {/* DSCR Chart */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground mb-1">DSCR by Year</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Debt Service Coverage Ratio — red line = 1.5x minimum covenant
        </p>
        <DscrTrendChart />
      </div>

      {/* Payback Chart */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground mb-1">
          Cumulative Cash Flow — Payback Visualization
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Project FCF cumulative · Breakeven = payback point
        </p>
        <PaybackChart />
      </div>

      {/* Returns notes */}
      <div className="bg-muted/50 border border-border rounded-xl p-4">
        <p className="text-xs font-semibold text-foreground mb-2">Notes on Return Metrics</p>
        <ul className="space-y-1">
          {[
            'Project IRR is calculated on project free cash flows (unlevered — before debt service).',
            'Equity IRR is after debt service — highly sensitive to actual repayment schedule.',
            'Interest and principal schedules are simplified for modelling; final bank schedule will differ.',
            'Carbon credit revenue and MNRE CFA are excluded from Base Case (Upside only).',
            'Working capital modelled as initial lump sum; ongoing WC changes not modelled monthly.',
            '10-year explicit forecast; no terminal value added to NPV or IRR.',
          ].map((note, idx) => (
            <li
              key={`returns-note-${idx}`}
              className="text-xs text-muted-foreground flex items-start gap-1.5"
            >
              <span className="mt-0.5">•</span>
              {note}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
